/* tslint:disable:no-empty */
import React, { MutableRefObject, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { IBMLocale } from 'src/common/locale/mapValidLocale';
import { useSmallBreakpoint } from 'src/common/hooks/widgets/useBreakpoint';
import { useEnv } from 'src/common/hooks/widgets/useEnv';
import { useCookiesURL } from 'src/common/hooks/widgets/useCookiesURL';
import * as URX_FORM from '../constants/urx-form';
import { COMBINED_USERS, UT30MaximoSaaS } from '../constants/spread';
import { Helmet } from 'react-helmet';
import {
  Button,
  ButtonSet,
  ContentSwitcher,
  InlineNotification,
  NumberInput,
  RadioTile,
  SkeletonPlaceholder,
  Switch,
  Tag,
  TileGroup,
  Toggle,
  Tooltip,
  Modal,
} from 'carbon-components-react';
import './MASSaaSPriceEstimator.scss';
import 'src/common/style/common.scss';
import { usePrice } from '../hooks/usePrice';
import Cookies from 'js-cookie';
import Footnote from './Footnote';
import messages from '../locales/messages';
import Thanks from './Thanks';
import { segmentsTracking } from 'src/common/lib/tracker';
import { initUrxForm } from 'src/common/hooks/widgets/urxForm';
import { swapCountryAndLanguage } from 'src/common/utils/swapCountryAndLanguage';
import { useBPName } from 'src/common/hooks/widgets/useBPName';
import BusinessPartner from 'src/common/components/BusinessPartner/BusinessPartner';
import {
  getMessage,
  useLang,
  determineCountry,
  LangDefaults,
} from 'src/common/hooks/widgets/useLang';
import {
  Package,
  Tiles,
  aiServiceVal,
  authorizedUsers,
  concurrentUsers,
  essentialsEnv,
  essentialsUsers,
  estInspectionTableData,
  estMaintenanceTableData,
  healthScores,
  leaseNumSize,
  standardAppPoints,
  switchBrand,
} from '../constants/default';
import { useNumberFormat } from 'src/common/hooks/widgets/useNumberFormat';
import { FormModal } from 'src/common/utils/FormModal';
import { Launch16 } from '@carbon/icons-react';
import buyNowCountryData from '../data/buy.json';
import { shortcode } from '../MASSaaSPriceEstimator.widget';
import countryList from '../services/currencies.json';
import DisclaimerText from 'src/common/components/DisclaimerText/DisclaimerText';
import {
  convertToString,
  createUseTranslation,
} from '../../../common/hooks/widgets/useTranslation';
import { useMASSAASSelector } from '../lib/redux/hooks';

export interface MASSaaSPriceEstimatorProps {
  langCode: string;
  test: boolean;
  countryToTest: string;
}

const MASSaaSPriceEstimator = ({
  langCode,
  test,
  countryToTest,
}: MASSaaSPriceEstimatorProps) => {
  const localeCode = swapCountryAndLanguage(langCode as IBMLocale);
  const [urxlang] = useState(localeCode.toString());
  const [drawerOpen, setDrawerOpen] = useState(false);
  const isSmall = useSmallBreakpoint('560px');
  const [environment] = useEnv();
  const [formComplete, setFormComplete] = useState(false);
  const [tileValueSelected, setTileValueSelected] = useState(Tiles.tile1);
  const [packageName, setPackageName] = useState(Package.pkg1);
  const [userAmount, setUserAmount] = useState(essentialsUsers.default);
  const [envAmount, setEnvAmount] = useState(essentialsEnv.default);
  const [showBuy, setShowBuy] = useState(false);
  const [buyNowUrl, setBuyNowUrl] = useState('');
  const [concurrentUsersAmt, setConcurrentUsersAmt] = useState(
    concurrentUsers.default,
  );
  const [authUsersAmt, setAuthUsersAmt] = useState(authorizedUsers.default);
  const [healthScoresAmt, setHealthScoresAmt] = useState(healthScores.default);
  const [additionalUsers, setAdditionalUsers] = useState(false);
  const [nonProdEnv, setNonProdEnv] = useState(false);
  const [aiService, setAiService] = useState(false);
  const concurrentUserRef = useRef() as MutableRefObject<HTMLInputElement>;
  const authUserRef = useRef() as MutableRefObject<HTMLInputElement>;
  const healthScoresRef = useRef() as MutableRefObject<HTMLInputElement>;
  const cookieURL = useCookiesURL(shortcode);
  const [open, setOpen] = useState(false);
  const [onInspection, setOnInspection] = useState(false);
  const [whichEssentialPackage, setWhichEssentialPackage] = useState<number>(0); //need this to store the current essential package
  const [totalStdBaseAppPoints, setTotalStdBaseAppPoints] = useState(
    standardAppPoints.default,
  );
  const [currency, setCurrency] = useState('');
  const [country, setCountry] = useState('');
  const [formID, setFormID] = useState('');
  const [offering, setOffering] = useState('Maintenance');
  const [spaceMgmtUser, setSpaceMgmtUser] = useState(false);
  const [spaceMgmtAddEnv, setSpaceMgmtAddEnv] = useState(false);
  const [captPlanUser, setCaptPlanUser] = useState(false);
  const [captPlanAddEnv, setCaptPlanAddEnv] = useState(false);
  const [leaseUser, setLeaseUser] = useState(false);
  const [leaseAddEnv, setLeaseAddEnv] = useState(false);
  const [leaseNum, setLeaseNum] = useState(false);
  const [pkg, setPkg] = useState('Essentials');

  // lang and userlocale is based out of ddo
  const [lang, userLocale, ddo] = useLang(
    countryList,
    test,
    countryToTest,
    setCurrency,
    setCountry,
    localeCode,
  );

  const [
    totalCost,
    totalCostMonthly,
    loading,
    totalEstAppPoints,
    _,
    inventSizeVal,
  ] = usePrice({
    userAmount,
    envAmount,
    tileValueSelected,
    additionalUsers,
    nonProdEnv,
    onInspection,
    currency,
    localeCode,
    concurrentUsersAmt,
    authUsersAmt,
    healthScoresAmt,
    setTotalStdBaseAppPoints,
    country,
    offering,
    spaceMgmtUser,
    spaceMgmtAddEnv,
    captPlanUser,
    captPlanAddEnv,
    leaseUser,
    leaseAddEnv,
    leaseNum,
    aiService,
    setPkg,
  });
  const [formattedAuthUsers] = useNumberFormat(authUsersAmt);
  const [formattedHealthScores] = useNumberFormat(healthScoresAmt);
  const [formattedStdBaseAppPoints] = useNumberFormat(totalStdBaseAppPoints);

  const { bpName, logo, companyId, desc } = useBPName(UT30MaximoSaaS, true);

  const [localCurrency, setlocalCurrency] = useState<string>(localeCode);

  const [toggleCountry, setToggleCountry] = useState(false);
  const [modalLocalCountry, setModalLocalCountry] = useState('');
  const [modalLocalCurrency, setModalLocalCurrency] = useState('USD');
  const [modalLocalLanguage, setModalLocalLanguage] = useState('English');

  const [localExist, setLocalExist] = useState(false);
  let localStorageFound = localStorage.getItem('langLocale');
  if (localStorageFound === null)
    localStorageFound = LangDefaults.localCountryLang;
  const [selectedLocale, setSelectedLocale] = useState(localStorageFound);
  const AEMMessages = useMASSAASSelector(
    (state) => state.translations.messages,
  );
  const useTranslation = createUseTranslation(messages, AEMMessages);

  // Determine Country
  determineCountry(
    setLocalExist,
    setSelectedLocale,
    setModalLocalCountry,
    setModalLocalLanguage,
    setModalLocalCurrency,
    setToggleCountry,
    lang,
    countryList,
    true,
    test,
    localeCode,
  );

  useEffect(() => {
    setlocalCurrency(userLocale); // show local Currency
  }, [userLocale, localeCode, localCurrency]);

  const invalidConcurrentUsers =
    parseInt(concurrentUserRef?.current?.value) < concurrentUsers.min ||
    parseInt(concurrentUserRef?.current?.value) > concurrentUsers.max ||
    concurrentUserRef?.current?.value === '';

  const invalidAuthUsers =
    parseInt(authUserRef?.current?.value) < authorizedUsers.min ||
    parseInt(authUserRef?.current?.value) > authorizedUsers.max ||
    authUserRef?.current?.value === '';

  const invalidHealthScores =
    parseInt(healthScoresRef?.current?.value) < healthScores.min ||
    parseInt(healthScoresRef?.current?.value) > healthScores.max ||
    healthScoresRef?.current?.value === '';

  window.urxEnvironment = environment;
  window.onUrxFormSubmitSuccessMultiple =
    window.onUrxFormSubmitSuccessMultiple || {};
  window.onUrxFormSubmitSuccessMultiple[URX_FORM.INSTANCE_ID] = function () {
    setFormComplete(true);
    setDrawerOpen(false);
  };

  useEffect(() => {
    // On pre-prod and prod widget will have the the parent div with shortcode classname.
    // So we are applying margin because some AEM classname causing style issue.
    const shortCodeStyle = Array.from(
      document.getElementsByClassName(
        shortcode,
      ) as HTMLCollectionOf<HTMLElement>,
    )[0];
    // In local shortcode will be alway undefined, to avoid app crash on local we are
    // checking the below condition and it will only work in pre-prod and prod version.
    if (shortCodeStyle !== undefined) {
      shortCodeStyle.style.marginLeft = '1rem';
    }
  }, []);

  useEffect(() => {
    setCookie();
  }, [
    userAmount,
    envAmount,
    totalCostMonthly,
    totalCost,
    packageName,
    formID,
    tileValueSelected,
    onInspection,
    offering,
    captPlanAddEnv,
    captPlanUser,
    spaceMgmtAddEnv,
    spaceMgmtUser,
    leaseAddEnv,
    leaseUser,
    leaseNum,
    aiService,
  ]);

  useEffect(() => {
    if (tileValueSelected === Tiles.tile2) {
      setConcurrentUsersAmt(concurrentUsers.default);
      setAuthUsersAmt(authorizedUsers.default);
      setHealthScoresAmt(healthScores.default);
    } else if (tileValueSelected === Tiles.tile1) {
      setUserAmount(essentialsUsers.default);
      setEnvAmount(essentialsEnv.default);
      setAdditionalUsers(false);
      setNonProdEnv(false);
    }
  }, [tileValueSelected]);

  useEffect(() => {
    if (ddo) {
      const item = buyNowCountryData.find((i) => i.ddoLoc === ddo);
      const selected = item || buyNowCountryData.find((i) => i.ddoLoc === 'us');
      if (!onInspection) {
        setBuyNowUrl(selected!.maintenanceUrl);
      } else {
        setBuyNowUrl(selected!.inspectionUrl);
      }
      setShowBuy(true);
    }
  }, [ddo, tileValueSelected, onInspection]);

  const setCookie = () => {
    const essentialsPkgInfo =
      offering === switchBrand.brand1
        ? 'Maintenance Essentials'
        : offering === switchBrand.brand2
        ? 'Inspection Essentials'
        : offering === switchBrand.brand3
        ? 'Space Management Essentials'
        : offering === switchBrand.brand4
        ? 'Capital Planning Essentials'
        : offering === switchBrand.brand6
        ? 'Inventory Optimization Essentials'
        : 'Lease Management Essentials';
    const essentialsInfo =
      offering === switchBrand.brand1
        ? ' Users: ' +
          userAmount +
          ' | Environment: ' +
          envAmount +
          ' | Work Orders / Hour: ' +
          '100' +
          ' | Health Scores / Hour: ' +
          '1,000' +
          ' | AI Service: ' +
          (aiService ? aiServiceVal.max : aiServiceVal.min)
        : cookieText1 +
          ': ' +
          tableVal1 +
          ' | ' +
          cookieText2 +
          ': ' +
          tableVal2 +
          (cookieText3 && tableText3 ? ' | ' : '') +
          (cookieText3 && tableText3 ? cookieText3 + ': ' + tableVal3 : '');
    const packageInfo =
      tileValueSelected === Tiles.tile1
        ? essentialsInfo + ' | Package: ' + essentialsPkgInfo
        : ' Concurrent Users: ' +
          concurrentUsersAmt +
          ' | Authorized Users: ' +
          formattedAuthUsers +
          ' | Health Scores: ' +
          formattedHealthScores +
          ' | AppPoints: ' +
          formattedStdBaseAppPoints +
          ' | Package: ' +
          packageName;

    Cookies.set(
      'urxdcq',
      JSON.stringify({
        source: formID,
        // One field to provide all values (stringify)
        dcq: {
          Q_NOTESDATA:
            cookieURL +
            packageInfo +
            ' | Estimated Monthly cost: ' +
            totalCostMonthly +
            ' | Estimated Annual cost: ' +
            totalCost,
        },
      }),
    );
  };

  const handleTakeMe = () => {
    if (tileValueSelected === Tiles.tile1) {
      setTileValueSelected(Tiles.tile2);
    } else if (tileValueSelected === Tiles.tile2) {
      setTileValueSelected(Tiles.tile3);
    } else {
      setTileValueSelected(Tiles.tile1);
    }
  };

  const essentialsFeaturesArray = [
    [
      useTranslation('est_mnt_feat_pt1') as string,
      useTranslation('est_mnt_feat_pt2') as string,
      useTranslation('est_mnt_feat_pt3') as string,
      useTranslation('est_mnt_feat_pt4') as string,
      useTranslation('est_mnt_feat_pt5') as string,
    ],
    [
      useTranslation('est_insp_feat_pt1') as string,
      useTranslation('est_insp_feat_pt2') as string,
    ],
    [
      useTranslation('est_lease_feat_pt1') as string,
      useTranslation('est_lease_feat_pt2') as string,
    ],
    [
      useTranslation('est_space_feat_pt1') as string,
      useTranslation('est_space_feat_pt2') as string,
    ],
    [
      useTranslation('est_capt_feat_pt1') as string,
      useTranslation('est_capt_feat_pt2') as string,
    ],
    [
      useTranslation('est_invopt_feat_pt1') as string,
      useTranslation('est_invopt_feat_pt2') as string,
      useTranslation('est_invopt_feat_pt3') as string,
      useTranslation('est_invopt_feat_pt4') as string,
    ],
  ];

  const standardFeature = [
    useTranslation('std_feat_pt1') as string,
    useTranslation('std_feat_pt2') as string,
    useTranslation('std_feat_pt3') as string,
    useTranslation('std_feat_pt4') as string,
  ];

  const premiumFeature = [
    useTranslation('prem_feat_pt1') as string,
    useTranslation('prem_feat_pt2') as string,
    useTranslation('prem_feat_pt3') as string,
    useTranslation('prem_feat_pt4') as string,
  ];

  const recommendText =
    tileValueSelected === Tiles.tile1
      ? useTranslation('recommend_text_std')
      : useTranslation('recommend_text_prem');
  const premNotificationLabel = useTranslation('prem_notif_label');

  const combinedTotalUsers = Math.round(authUsersAmt / 3) + concurrentUsersAmt;
  const yes = useTranslation('yes') as string;
  const no = useTranslation('no') as string;

  const appIncludeInfo = [
    useTranslation('manage') as string,
    useTranslation('health') as string,
  ];

  type ModalStateManagerProps = {
    renderLauncher: any;
    children: React.ComponentType<LauncherContentProps>;
  };

  type LauncherContentProps = {
    open: boolean;
    setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  };

  const ModalStateManager: React.FC<ModalStateManagerProps> = ({
    renderLauncher: LauncherContent,
    children: ModalContent,
  }) => {
    return (
      <>
        {!ModalContent || typeof document === 'undefined'
          ? null
          : ReactDOM.createPortal(
              <ModalContent open={open} setOpen={setOpen} />,
              document.body,
            )}
        {LauncherContent && <LauncherContent open={open} setOpen={setOpen} />}
      </>
    );
  };

  const handleModal1 = () => {
    setOpen(true);
    segmentsTracking('CTA Clicked', {
      pageTitle: 'Maximo SaaS Pricing Page',
      object: 'Pricing page',
      resultValue: 'CTA clicked',
      CTA: 'Modal open ' + modalLocalCountry,
      location: 'Estimator',
      action: '',
      field: '',
    });
  };

  useEffect(() => {
    if (localeCode.includes('ar')) {
      document.documentElement.dir = 'rtl';
    } else {
      document.documentElement.dir = 'ltr';
    }
  }, [localeCode]);

  interface MyObjectType {
    tableText1: string;
    tableText2: string;
    tableText3: string;
    tableVal1: string;
    tableVal2: string;
    tableVal3: string;
    stateVal1: boolean;
    stateVal2: boolean;
    stateVal3: boolean;
    setState1: React.Dispatch<React.SetStateAction<boolean>>;
    setState2: React.Dispatch<React.SetStateAction<boolean>>;
    setState3: React.Dispatch<React.SetStateAction<boolean>>;
    cookieText1: string;
    cookieText2: string;
    cookieText3: string;
    labelText: string;
    helperText: string;
  }

  const getValues = (value: string) => {
    const noop: React.Dispatch<React.SetStateAction<boolean>> = () => {
      // left empty
    };
    let obj: MyObjectType = {
      tableText1: '',
      tableText2: '',
      tableText3: '',
      tableVal1: '',
      tableVal2: '',
      tableVal3: '',
      stateVal1: false,
      stateVal2: false,
      stateVal3: false,
      setState1: noop,
      setState2: noop,
      setState3: noop,
      cookieText1: '',
      cookieText2: '',
      cookieText3: '',
      labelText: '',
      helperText: '',
    };
    switch (value) {
      case switchBrand.brand2:
        obj = {
          tableText1: useTranslation('client_device_text') as string,
          tableText2: useTranslation('inference_text') as string,
          tableText3: useTranslation('storage_text') as string,
          tableVal1: estInspectionTableData.clientDevice,
          tableVal2: estInspectionTableData.inferencesHour,
          tableVal3: estInspectionTableData.storage,
          stateVal1: false,
          stateVal2: false,
          stateVal3: false,
          setState1: () => {
            // left empty
          },
          setState2: () => {
            // left empty
          },
          setState3: () => {
            // left empty
          },
          cookieText1: 'Client Devices',
          cookieText2: 'Inferences / Hour',
          cookieText3: 'Storage',
          labelText: useTranslation('toggle1_info_pt1') as string,
          helperText: useTranslation('toggle1_info_pt2') as string,
        };
        break;
      case switchBrand.brand3:
        obj = {
          tableText1: useTranslation('user_text') as string,
          tableText2: useTranslation('env_text') as string,
          tableText3: useTranslation('reserv_text') as string,
          tableVal1: spaceMgmtUser
            ? String(essentialsUsers.max)
            : String(essentialsUsers.default - 5),
          tableVal2: spaceMgmtAddEnv
            ? String(essentialsEnv.max)
            : String(essentialsEnv.default),
          tableVal3: '2000',
          stateVal1: spaceMgmtUser,
          stateVal2: spaceMgmtAddEnv,
          stateVal3: false,
          setState1: setSpaceMgmtUser,
          setState2: setSpaceMgmtAddEnv,
          setState3: () => {
            // left empty
          },
          cookieText1: 'Users',
          cookieText2: 'Environments',
          cookieText3: 'Reservations',
          labelText: useTranslation('toggle_helper_update_text') as string,
          helperText: useTranslation('toggle_helper_desc_text') as string,
        };
        break;
      case switchBrand.brand4:
        obj = {
          tableText1: useTranslation('user_text') as string,
          tableText2: useTranslation('env_text') as string,
          tableText3: '',
          tableVal1: captPlanUser
            ? String(essentialsUsers.max)
            : String(essentialsUsers.default),
          tableVal2: captPlanAddEnv
            ? String(essentialsEnv.max)
            : String(essentialsEnv.default),
          tableVal3: '',
          stateVal1: captPlanUser,
          stateVal2: captPlanAddEnv,
          stateVal3: false,
          setState1: setCaptPlanUser,
          setState2: setCaptPlanAddEnv,
          setState3: () => {
            // left empty
          },
          cookieText1: 'Users',
          cookieText2: 'Environments',
          cookieText3: '',
          labelText: useTranslation('toggle1_info_pt1') as string,
          helperText: useTranslation('toggle1_info_pt2') as string,
        };
        break;
      case switchBrand.brand5:
        obj = {
          tableText1: useTranslation('user_text') as string,
          tableText2: useTranslation('env_text') as string,
          tableText3: useTranslation('leases_text') as string,
          tableVal1: leaseUser
            ? String(essentialsUsers.max)
            : String(essentialsUsers.default - 5),
          tableVal2: leaseAddEnv
            ? String(essentialsEnv.max)
            : String(essentialsEnv.default),
          tableVal3: leaseNum
            ? String(leaseNumSize.max)
            : String(leaseNumSize.default),
          stateVal1: leaseUser,
          stateVal2: leaseAddEnv,
          stateVal3: leaseNum,
          setState1: setLeaseUser,
          setState2: setLeaseAddEnv,
          setState3: setLeaseNum,
          cookieText1: 'Users',
          cookieText2: 'Environments',
          cookieText3: 'Leases',
          labelText: useTranslation('toggle_helper_update_text') as string,
          helperText: useTranslation('toggle_helper_desc_text') as string,
        };
        break;
      case switchBrand.brand6:
        obj = {
          tableText1: useTranslation('env_text') as string,
          tableText2: useTranslation('invent_size_text') as string,
          tableText3: useTranslation('invent_item_text') as string,
          tableVal1: '1',
          tableVal2: '50 million (USD)',
          tableVal3: '10,000',
          stateVal1: false,
          stateVal2: false,
          stateVal3: false,
          setState1: () => {
            // empty
          },
          setState2: () => {
            // empty
          },
          setState3: () => {
            // left empty
          },
          cookieText1: 'Environments',
          cookieText2: 'Inventory size value',
          cookieText3: 'Inventory item records',
          labelText: useTranslation('toggle1_info_pt1') as string,
          helperText: useTranslation('toggle1_info_pt2') as string,
        };
    }
    return obj;
  };
  const {
    tableText1,
    tableText2,
    tableText3,
    tableVal1,
    tableVal2,
    tableVal3,
    stateVal1,
    stateVal2,
    stateVal3,
    setState1,
    setState2,
    setState3,
    cookieText1,
    cookieText2,
    cookieText3,
    labelText,
    helperText,
  } = getValues(offering);

  const notifState =
    offering === switchBrand.brand2 ||
    offering === switchBrand.brand6 ||
    stateVal1 ||
    stateVal2 ||
    stateVal3;
  return (
    <div className="mas_saas">
      <Helmet>
        <script
          type="application/javascript"
          src={`https://www.ibm.com/account/ibmidutil/widget/js/loader.js`}
        ></script>
        <script
          type="application/javascript"
          src={`https://www.ibm.com/account/ibmidutil/widget/js/main.js`}
        ></script>
      </Helmet>
      {formComplete ? (
        <Thanks
          usersAmount={userAmount}
          envCount={envAmount}
          healthScoresAmount={formattedHealthScores}
          concurrentAmount={concurrentUsersAmt}
          authorizedAmount={formattedAuthUsers}
          appPoints={formattedStdBaseAppPoints}
          onInspection={onInspection}
          tileValue={tileValueSelected}
          licensePriceMonthly={`${totalCostMonthly}`}
          packageName={packageName}
          bpName={bpName}
          aiService={aiService ? aiServiceVal.max : aiServiceVal.min}
          tableText1={tableText1}
          tableText2={tableText1}
          tableText3={tableText1}
          tableVal1={tableVal1}
          tableVal2={tableVal2}
          tableVal3={tableVal3}
          offering={offering}
        />
      ) : (
        <div id="MASSaaSGrid" className="bx--grid widget-styles">
          {bpName ? (
            <BusinessPartner bpName={bpName} logo={logo} desc={desc} />
          ) : null}
          <div className="bx--row mar3">
            <div
              className="bx--col-lg-4 bx--col-md-8 bx--col-sm-8 bx--offset-lg-12"
              id="countryModal"
            >
              {localExist && toggleCountry ? (
                <ModalStateManager
                  renderLauncher={() => (
                    <Button onClick={handleModal1}>
                      Access Page in
                      {' ' + modalLocalLanguage}
                    </Button>
                  )}
                >
                  {({ open, setOpen }) => (
                    <Modal
                      open={open}
                      onRequestClose={() => setOpen(false)}
                      modalLabel={`Found country ${modalLocalCountry}. Access page in ${modalLocalLanguage} (${modalLocalCurrency})`}
                      modalHeading={`The Maximo PE SaaS Price estimator is available in ${modalLocalLanguage} with local currency ${modalLocalCurrency}`}
                      size={'sm'}
                      primaryButtonText={'Cool! Take me there'}
                      secondaryButtonText={'Stay here'}
                      onRequestSubmit={() => {
                        localStorage.setItem('langLocale', selectedLocale);
                        (window.location.href =
                          'https://www.ibm.com/' +
                          selectedLocale +
                          '/products/maximo/pricing'),
                          segmentsTracking('CTA Clicked', {
                            pageTitle: 'Maximo SaaS Pricing Page',
                            object: 'Pricing page',
                            resultValue: 'CTA clicked',
                            CTA: 'Switched to country ' + modalLocalCountry,
                            location: 'Estimator',
                            action: '',
                            field: '',
                          });
                        return true;
                      }}
                      onSecondarySubmit={() => {
                        segmentsTracking('CTA Clicked', {
                          pageTitle: 'Maximo SaaS Pricing Page',
                          object: 'Pricing page',
                          resultValue: 'CTA clicked',
                          CTA: 'Stay here button clicked ' + modalLocalCountry,
                          location: 'Estimator',
                          action: '',
                          field: '',
                        });
                        setOpen(false);
                        return true;
                      }}
                    ></Modal>
                  )}
                </ModalStateManager>
              ) : null}
            </div>
            <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-4">
              <div className="page_sub_heading ">
                {useTranslation('maas_saas_headline')}
              </div>
              <p className="page_body padding_24_3 mar2 ">
                {useTranslation('maas_saas_sub_headline')}
              </p>
              <div className="bx--row">
                <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-4">
                  <TileGroup
                    className="mas_saas__tilegroup"
                    name="radio tile group"
                    valueSelected={tileValueSelected}
                  >
                    <RadioTile
                      className="mas_saas__tile"
                      id="radio-tile-1-mas_saas"
                      value="essentials"
                      onClick={() => {
                        setTileValueSelected(Tiles.tile1);
                        setPackageName(Package.pkg1);
                        segmentsTracking('CTA Clicked', {
                          pageTitle: 'Maximo SaaS Pricing Page',
                          object: 'Pricing page',
                          resultValue: 'CTA clicked',
                          CTA: 'Essentials Tile',
                          location: 'Estimator',
                          action: '',
                          field: '',
                        });
                      }}
                    >
                      <div className="page_sub_heading mar8">
                        {Package.pkg1}
                      </div>
                      <div className="page_body_2">
                        {useTranslation('est_pkg_desc')}
                      </div>
                    </RadioTile>
                    <RadioTile
                      id="radio-tile-2-mas_saas"
                      value="standard"
                      className="mas_saas__tile"
                      onClick={() => {
                        setTileValueSelected(Tiles.tile2);
                        setPackageName(Package.pkg2);
                        segmentsTracking('CTA Clicked', {
                          pageTitle: 'Maximo SaaS Pricing Page',
                          object: 'Pricing page',
                          resultValue: 'CTA clicked',
                          CTA: 'Standard Tile',
                          location: 'Estimator',
                          action: '',
                          field: '',
                        });
                      }}
                    >
                      <div className="page_sub_heading mar8">
                        {Package.pkg2}
                      </div>
                      <div className="page_body_2">
                        {useTranslation('std_pkg_desc')}
                      </div>
                    </RadioTile>
                    <RadioTile
                      id="radio-tile-3-mas_saas"
                      value="premium"
                      onClick={() => {
                        setTileValueSelected(Tiles.tile3);
                        setPackageName(Package.pkg3);
                        segmentsTracking('CTA Clicked', {
                          pageTitle: 'Maximo SaaS Pricing Page',
                          object: 'Pricing page',
                          resultValue: 'CTA clicked',
                          CTA: 'Premium Tile',
                          location: 'Estimator',
                          action: '',
                          field: '',
                        });
                      }}
                    >
                      <div className="page_sub_heading mar8">
                        {Package.pkg3}
                      </div>
                      <div className="page_body_2">
                        {useTranslation('prem_pkg_desc')}
                      </div>
                    </RadioTile>
                  </TileGroup>
                </div>
              </div>
              {tileValueSelected === Tiles.tile1 && (
                <div className="bx--row mar9">
                  <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                    <ContentSwitcher
                      className="content-switcher"
                      // selectedIndex={onInspection ? 1 : 0}
                      selectedIndex={whichEssentialPackage}
                      size="lg"
                      onChange={(obj) => {
                        const { name } = obj;
                        setOffering(String(name));
                        segmentsTracking('CTA Clicked', {
                          pageTitle: 'Maximo SaaS Pricing Page',
                          object: 'Pricing page',
                          resultValue: `CTA clicked ${name}`,
                          CTA: 'Essential switch',
                          location: 'Estimator',
                          action: '',
                          field: '',
                        });
                        setWhichEssentialPackage(obj.index ? obj.index : 0);
                        setOnInspection(
                          name === switchBrand.brand1 ? false : true,
                        );
                      }}
                    >
                      <Switch
                        text={convertToString(
                          useTranslation('brand1_label', true),
                        )}
                        name={switchBrand.brand1}
                      />
                      <Switch
                        text={convertToString(
                          useTranslation('brand2_label', true),
                        )}
                        name={switchBrand.brand2}
                      />
                      <Switch
                        text={convertToString(
                          useTranslation('brand5_label', true),
                        )}
                        name={switchBrand.brand5}
                      />
                      <Switch
                        text={convertToString(
                          useTranslation('brand3_label', true),
                        )}
                        name={switchBrand.brand3}
                      />
                      <Switch
                        text={convertToString(
                          useTranslation('brand4_label', true),
                        )}
                        name={switchBrand.brand4}
                      />
                      <Switch
                        text={convertToString(
                          useTranslation('brand6_label', true),
                        )}
                        name={switchBrand.brand6}
                      />
                    </ContentSwitcher>
                  </div>
                </div>
              )}
              <div className="bx--row mar9">
                <div className="bx--col-lg-10 bx--col-md-16 bx--col-sm-16">
                  <div className="mar3">
                    {tileValueSelected === Tiles.tile1 ? (
                      <>
                        <div className="page_body_2 ">
                          {useTranslation('est_feat_label')}
                        </div>
                        <div>
                          {essentialsFeaturesArray[whichEssentialPackage].map(
                            (item: string, index: number) => (
                              <Tag
                                key={index}
                                type={'purple'}
                                size="md"
                                disabled={false}
                                filter={false}
                                className="tag-margin"
                              >
                                {item}
                              </Tag>
                            ),
                          )}
                        </div>
                      </>
                    ) : tileValueSelected === Tiles.tile2 ? (
                      <>
                        <div className="page_body_2 ">
                          {useTranslation('std_feat_label')}:
                        </div>
                        <div>
                          {standardFeature.map(
                            (item: string, index: number) => (
                              <Tag
                                key={index}
                                type={'purple'}
                                size="md"
                                disabled={false}
                                filter={false}
                                className="tag-margin"
                              >
                                {item}
                              </Tag>
                            ),
                          )}
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="page_body_2 ">
                          {useTranslation('prem_feat_label')}:
                        </div>
                        <div>
                          {premiumFeature.map((item: string, index) => (
                            <Tag
                              key={index}
                              type={'purple'}
                              size="md"
                              disabled={false}
                              filter={false}
                              className="tag-margin"
                            >
                              {item}
                            </Tag>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                  <div
                    className={`bx--row mar3 ${
                      tileValueSelected === Tiles.tile3 ? 'mar5' : ''
                    }`}
                  >
                    {tileValueSelected === Tiles.tile1 ? (
                      <>
                        <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16 mar8 ">
                          {whichEssentialPackage === 5 && (
                            <div className="page_body price_table_comp__requirement">
                              {useTranslation('invent_optm_text')}
                            </div>
                          )}
                          {whichEssentialPackage > 2 &&
                            whichEssentialPackage < 5 && (
                              <div className="bx--row max-mar1">
                                <div className="bx--col-lg-8 bx--col-md-3 bx--col-sm-16">
                                  <div className="slide_text">
                                    {useTranslation('toggle1_text')}
                                  </div>
                                  <Toggle
                                    size="md"
                                    labelText={labelText}
                                    labelA={no}
                                    labelB={yes}
                                    id="user"
                                    toggled={stateVal1}
                                    onChange={() => {
                                      segmentsTracking('CTA Clicked', {
                                        pageTitle: 'Maximo SaaS Pricing Page',
                                        object: 'Pricing page',
                                        resultValue: 'CTA clicked',
                                        CTA: 'Additional user toggle',
                                        location: 'Estimator',
                                        action: '',
                                        field: '',
                                      });
                                      setState1(!stateVal1);
                                    }}
                                  />
                                  <p className="helper-text ">{helperText}</p>
                                </div>
                                <div className="bx--col-lg-8 bx--col-md-3 bx--col-sm-16">
                                  <div className="page_body_2">
                                    {useTranslation('toggle2_text')}
                                  </div>
                                  <Toggle
                                    size="md"
                                    labelText={
                                      useTranslation(
                                        'toggle2_info_pt1',
                                      ) as string
                                    }
                                    labelA={no}
                                    labelB={yes}
                                    id="non-prod env"
                                    toggled={stateVal2}
                                    onChange={() => {
                                      segmentsTracking('CTA Clicked', {
                                        pageTitle: 'Maximo SaaS Pricing Page',
                                        object: 'Pricing page',
                                        resultValue: 'CTA clicked',
                                        CTA: 'Non-Prod Env toggle',
                                        location: 'Estimator',
                                        action: '',
                                        field: '',
                                      });
                                      setState2(!stateVal2);
                                    }}
                                  />
                                  <p className="helper-text">
                                    {useTranslation('toggle2_info_pt2')}
                                  </p>
                                </div>
                              </div>
                            )}
                          {whichEssentialPackage === 2 && (
                            <div className="bx--row max-mar1">
                              <div className="bx--col-lg-5 bx--col-md-3 bx--col-sm-16">
                                <div className="slide_text">
                                  {useTranslation('toggle1_text')}
                                </div>
                                <Toggle
                                  size="md"
                                  labelText={labelText}
                                  labelA={no}
                                  labelB={yes}
                                  id="user"
                                  toggled={stateVal1}
                                  onChange={() => {
                                    segmentsTracking('CTA Clicked', {
                                      pageTitle: 'Maximo SaaS Pricing Page',
                                      object: 'Pricing page',
                                      resultValue: 'CTA clicked',
                                      CTA: 'Additional user toggle',
                                      location: 'Estimator',
                                      action: '',
                                      field: '',
                                    });
                                    setState1(!stateVal1);
                                  }}
                                />
                                <p className="helper-text ">{helperText}</p>
                              </div>

                              <div className="bx--col-lg-5 bx--col-md-3 bx--col-sm-16">
                                <div className="page_body_2">
                                  {useTranslation('lease_toogle_label')}
                                </div>
                                <Toggle
                                  size="md"
                                  labelText={
                                    useTranslation(
                                      'lease_toogle_helper1',
                                    ) as string
                                  }
                                  labelA={no}
                                  labelB={yes}
                                  id="lease"
                                  toggled={stateVal3}
                                  onChange={() => {
                                    segmentsTracking('CTA Clicked', {
                                      pageTitle: 'Maximo SaaS Pricing Page',
                                      object: 'Pricing page',
                                      resultValue: 'CTA clicked',
                                      CTA: 'Non-Prod Env toggle',
                                      location: 'Estimator',
                                      action: '',
                                      field: '',
                                    });
                                    setState3(!stateVal3);
                                  }}
                                />
                                <p className="helper-text">
                                  {useTranslation('lease_toogle_helper2')}
                                </p>
                              </div>
                              <div className="bx--col-lg-6 bx--col-md-3 bx--col-sm-16">
                                <div className="page_body_2">
                                  {useTranslation('toggle2_text')}
                                </div>
                                <Toggle
                                  size="md"
                                  labelText={
                                    useTranslation('toggle2_info_pt1') as string
                                  }
                                  labelA={no}
                                  labelB={yes}
                                  id="non-prod env"
                                  toggled={stateVal2}
                                  onChange={() => {
                                    segmentsTracking('CTA Clicked', {
                                      pageTitle: 'Maximo SaaS Pricing Page',
                                      object: 'Pricing page',
                                      resultValue: 'CTA clicked',
                                      CTA: 'Non-Prod Env toggle',
                                      location: 'Estimator',
                                      action: '',
                                      field: '',
                                    });
                                    setState2(!stateVal2);
                                  }}
                                />
                                <p className="helper-text">
                                  {useTranslation('toggle2_info_pt2')}
                                </p>
                              </div>
                            </div>
                          )}
                        </div>
                        {!onInspection && (
                          <>
                            <div className="bx--col-lg-5 bx--col-md-16 bx--col-sm-16 mar1">
                              <div className="page_body_2 ">
                                {useTranslation('toggle1_text')}
                              </div>
                              <div>
                                <Toggle
                                  size="md"
                                  labelText={
                                    useTranslation('toggle1_info_pt1') as string
                                  }
                                  labelA={no}
                                  labelB={yes}
                                  id="additional-users"
                                  toggled={additionalUsers}
                                  onChange={() => {
                                    segmentsTracking('CTA Clicked', {
                                      pageTitle: 'Maximo SaaS Pricing Page',
                                      object: 'Pricing page',
                                      resultValue: 'CTA clicked',
                                      CTA: 'Additional User toggle',
                                      location: 'Estimator',
                                      action: '',
                                      field: '',
                                    });
                                    setAdditionalUsers(!additionalUsers);
                                    if (!additionalUsers) {
                                      setUserAmount(essentialsUsers.max);
                                    } else {
                                      setUserAmount(essentialsUsers.default);
                                    }
                                  }}
                                />
                              </div>
                              <p className="helper-text ">
                                {useTranslation('toggle1_info_pt2')}
                              </p>
                            </div>
                            {/* <div className="bx--col-lg-1" /> */}
                            <div className="bx--col-lg-6 bx--col-md-16 bx--col-sm-16">
                              <div className="page_body_2">
                                {useTranslation('toggle2_text')}
                              </div>
                              <Toggle
                                size="md"
                                labelText={
                                  useTranslation('toggle2_info_pt1') as string
                                }
                                labelA={no}
                                labelB={yes}
                                id="non-prod env"
                                toggled={nonProdEnv}
                                onChange={() => {
                                  segmentsTracking('CTA Clicked', {
                                    pageTitle: 'Maximo SaaS Pricing Page',
                                    object: 'Pricing page',
                                    resultValue: 'CTA clicked',
                                    CTA: 'Non-Prod Env toggle',
                                    location: 'Estimator',
                                    action: '',
                                    field: '',
                                  });
                                  setNonProdEnv(!nonProdEnv);
                                  if (!nonProdEnv) {
                                    setEnvAmount(essentialsEnv.max);
                                  } else {
                                    setEnvAmount(essentialsEnv.default);
                                  }
                                }}
                              />
                              <p className="helper-text">
                                {useTranslation('toggle2_info_pt2')}
                              </p>
                            </div>
                            <div className="bx--col-lg-5 bx--col-md-16 bx--col-sm-16">
                              <div className="page_body_2">
                                {useTranslation('aiService_text')}
                              </div>
                              <Toggle
                                size="md"
                                labelText={
                                  useTranslation('ai_desc_text') as string
                                }
                                labelA={no}
                                labelB={yes}
                                id="ai-service"
                                toggled={aiService}
                                onChange={() => {
                                  segmentsTracking('CTA Clicked', {
                                    pageTitle: 'Maximo SaaS Pricing Page',
                                    object: 'Pricing page',
                                    resultValue: 'CTA clicked',
                                    CTA: 'AI service toggle',
                                    location: 'Estimator',
                                    action: '',
                                    field: '',
                                  });
                                  setAiService(!aiService);
                                }}
                              />
                            </div>
                          </>
                        )}
                        {(additionalUsers || nonProdEnv) && !onInspection && (
                          <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16 mar8 ">
                            <InlineNotification
                              title={recommendText}
                              kind="info-square"
                              role="alert"
                              actions={
                                <p
                                  className="notification-text"
                                  onClick={handleTakeMe}
                                >
                                  {useTranslation('std_notif_txt')}
                                </p>
                              }
                            />
                          </div>
                        )}
                        {notifState && (
                          <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16 mar8 ">
                            <InlineNotification
                              title={recommendText}
                              kind="info-square"
                              role="alert"
                              actions={
                                <p
                                  className="notification-text"
                                  onClick={() => {
                                    segmentsTracking('CTA Clicked', {
                                      pageTitle: 'Maximo SaaS Pricing Page',
                                      object: 'Pricing page',
                                      resultValue: 'CTA clicked',
                                      CTA: 'Book a Consultation',
                                      location: 'Estimator',
                                      action: '',
                                      field: '',
                                    });
                                    window.open(
                                      ' https://www.ibm.com/products/maximo/pricing/?schedulerform=',
                                      '_self',
                                    );
                                  }}
                                >
                                  {useTranslation('book_text')}
                                </p>
                              }
                            />
                          </div>
                        )}
                      </>
                    ) : (
                      <>
                        <div className="bx--col-lg-8 bx--col-md-3 bx--col-sm-16 ">
                          <Tooltip
                            onChange={() => {
                              segmentsTracking('CTA Clicked', {
                                pageTitle: 'Maximo SaaS Pricing Page',
                                object: 'Pricing page',
                                resultValue: 'CTA clicked',
                                CTA: 'Concurrent Users tooltip',
                                location: 'Estimator',
                                action: '',
                                field: '',
                              });
                            }}
                            direction={isSmall ? 'bottom' : 'right'}
                            tabIndex={0}
                            triggerText={
                              <span className="page_body mb-3">
                                {useTranslation('slider1_text')}
                              </span>
                            }
                          >
                            <span className="slide_text">
                              {useTranslation('tooltip_concurrent_users_text')}
                            </span>
                          </Tooltip>
                          <NumberInput
                            id="concurrent-user"
                            size="md"
                            allowEmpty={true}
                            ref={concurrentUserRef}
                            invalidText={convertToString(
                              useTranslation('error_msg1', true),
                            )}
                            min={concurrentUsers.min}
                            max={concurrentUsers.max}
                            className="number-input mb-5"
                            disabled={tileValueSelected === Tiles.tile3}
                            value={concurrentUsersAmt}
                            onChange={() => {
                              segmentsTracking('CTA Clicked', {
                                pageTitle: 'Maximo SaaS Pricing Page',
                                object: 'Pricing page',
                                resultValue: 'CTA clicked',
                                CTA: 'Concurrent User Input',
                                location: 'Estimator',
                                action: '',
                                field: '',
                              });
                              setConcurrentUsersAmt(
                                Number(concurrentUserRef.current.value),
                              );
                            }}
                          />
                        </div>
                        <div className="bx--col-lg-8 bx--col-md-3 bx--col-sm-16">
                          <Tooltip
                            onChange={() => {
                              segmentsTracking('CTA Clicked', {
                                pageTitle: 'Maximo SaaS Pricing Page',
                                object: 'Pricing page',
                                resultValue: 'CTA clicked',
                                CTA: 'Authorized Users tooltip',
                                location: 'Estimator',
                                action: '',
                                field: '',
                              });
                            }}
                            direction={isSmall ? 'bottom' : 'right'}
                            tabIndex={0}
                            triggerText={
                              <span className="page_body mb-3">
                                {useTranslation('slider2_text')}
                              </span>
                            }
                          >
                            <span className="slide_text">
                              {useTranslation('tooltip_auth_user_text')}
                            </span>
                          </Tooltip>
                          <NumberInput
                            id="authorized-user"
                            size="md"
                            className="mb-5"
                            allowEmpty={true}
                            min={authorizedUsers.min}
                            max={authorizedUsers.max}
                            ref={authUserRef}
                            disabled={tileValueSelected === Tiles.tile3}
                            invalidText={convertToString(
                              useTranslation('error_msg2', true),
                            )}
                            value={authUsersAmt}
                            onChange={() => {
                              segmentsTracking('CTA Clicked', {
                                pageTitle: 'Maximo SaaS Pricing Page',
                                object: 'Pricing page',
                                resultValue: 'CTA clicked',
                                CTA: 'Authorized Users Input',
                                location: 'Estimator',
                                action: '',
                                field: '',
                              });
                              setAuthUsersAmt(
                                Number(authUserRef.current.value),
                              );
                            }}
                          />
                        </div>
                        <div className="bx--col-lg-8 bx--col-md-3 bx--col-sm-16">
                          <Tooltip
                            onChange={() => {
                              segmentsTracking('CTA Clicked', {
                                pageTitle: 'Maximo SaaS Pricing Page',
                                object: 'Pricing page',
                                resultValue: 'CTA clicked',
                                CTA: 'Health Scores tooltip',
                                location: 'Estimator',
                                action: '',
                                field: '',
                              });
                            }}
                            direction={isSmall ? 'bottom' : 'right'}
                            tabIndex={0}
                            triggerText={
                              <span className="page_body mb-3">
                                {useTranslation('slider3_text')}
                              </span>
                            }
                          >
                            <span className="slide_text">
                              {useTranslation('tooltip_health_text')}
                            </span>
                          </Tooltip>
                          <NumberInput
                            id="health-scores"
                            size="md"
                            allowEmpty={true}
                            min={healthScores.min}
                            max={healthScores.max}
                            ref={healthScoresRef}
                            step={healthScores.default}
                            disabled={tileValueSelected === Tiles.tile3}
                            invalidText={convertToString(
                              useTranslation('error_msg3', true),
                            )}
                            value={healthScoresAmt}
                            onChange={() => {
                              segmentsTracking('CTA Clicked', {
                                pageTitle: 'Maximo SaaS Pricing Page',
                                object: 'Pricing page',
                                resultValue: 'CTA clicked',
                                CTA: 'Health Scores Input',
                                location: 'Estimator',
                                action: '',
                                field: '',
                              });
                              setHealthScoresAmt(
                                Number(healthScoresRef.current.value),
                              );
                            }}
                          />
                        </div>
                      </>
                    )}
                  </div>
                  {tileValueSelected === Tiles.tile2 && (
                    <>
                      <div className="bx--row mar3">
                        <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16 ">
                          <div className="page_body_2">
                            {useTranslation('applications')}
                          </div>

                          {appIncludeInfo.map((item: string, index) => (
                            <Tag
                              key={index}
                              type={'teal'}
                              size="md"
                              disabled={false}
                              filter={false}
                              className="tag-margin"
                            >
                              {item}
                            </Tag>
                          ))}
                        </div>
                      </div>
                      <div className="bx--row mar3 mar1">
                        <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                          <p className="page_body ">
                            {useTranslation('widgetText_3')}
                          </p>
                        </div>
                      </div>
                      {(concurrentUsersAmt === concurrentUsers.max ||
                        authUsersAmt === authorizedUsers.max ||
                        healthScoresAmt === healthScores.max ||
                        combinedTotalUsers >= 500) &&
                        !invalidConcurrentUsers &&
                        !invalidHealthScores &&
                        !invalidAuthUsers && (
                          <InlineNotification
                            title={recommendText}
                            kind={'info-square'}
                            actions={
                              <p
                                className="notification-text"
                                onClick={handleTakeMe}
                              >
                                {useTranslation('prem_notif_txt')}
                              </p>
                            }
                          />
                        )}
                    </>
                  )}
                  {tileValueSelected !== Tiles.tile3 && (
                    <div id="footnote" className="div-only-lg">
                      <div className="bx--row">
                        <div className="bx--col-lg-16 ">
                          <div id="footnote" className="div-only-l">
                            <div className="bx--row">
                              <div className="bx--col-lg-16">
                                <div>
                                  <p>
                                    <span className="footnote_num">1</span>
                                  </p>
                                </div>
                                <div className="footnote_text">
                                  <DisclaimerText
                                    localeCode={localeCode}
                                    product={'Maximo Application Suite'}
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                <div className="bx--col-lg-6 bx--col-md-16 bx--col-sm-16">
                  {tileValueSelected === Tiles.tile3 ? (
                    <div>
                      <InlineNotification
                        kind="info"
                        role="on-book-consultation"
                        title={premNotificationLabel}
                        subtitle={useTranslation('contact_info')}
                        statusIconDescription="on-book-consultation"
                        hideCloseButton={true}
                      />

                      <ButtonSet className=" btn-set mar4 cta-btn-group">
                        <Button
                          id="request_btn"
                          target="_self"
                          data-ibm-contact="scheduler-link"
                          href="#"
                          onClick={() => {
                            segmentsTracking('CTA Clicked', {
                              pageTitle: 'Maximo SaaS Pricing Page',
                              object: 'Pricing page',
                              resultValue: 'CTA clicked',
                              CTA: 'Book a Consultation',
                              location: 'Estimator',
                              action: '',
                              field: '',
                            });
                            window.open(
                              ' https://www.ibm.com/products/maximo/pricing/?schedulerform=',
                              '_self',
                            );
                          }}
                        >
                          {useTranslation('book_text')}
                        </Button>
                      </ButtonSet>
                    </div>
                  ) : (
                    <div id="stickyNav" className="bx--row box-shadow">
                      {tileValueSelected === Tiles.tile1 ? (
                        <Tag type="outline" className="mar4 tag-marginleft">
                          {Package.pkg1}
                        </Tag>
                      ) : tileValueSelected === Tiles.tile2 ? (
                        <Tag type="outline" className="mar4 tag-marginleft">
                          {Package.pkg2}
                        </Tag>
                      ) : (
                        <Tag type="outline" className="mar4 tag-marginleft">
                          {Package.pkg3}
                        </Tag>
                      )}
                      <div className="sticky-nav-table price_table_comp bg_white">
                        <table className="cds--data-table cds--data-table--sm">
                          <thead>
                            <tr>
                              <th scope="col">
                                <div className="price_table_comp__heading">
                                  {useTranslation('col1_text')}
                                </div>
                              </th>
                              <th scope="col">
                                <div className="cds--table-header-label">
                                  <div className="price_table_comp__heading">
                                    {useTranslation('col2_text')}
                                  </div>
                                </div>
                              </th>
                            </tr>
                          </thead>
                          {tileValueSelected === Tiles.tile1 &&
                          whichEssentialPackage === 0 ? (
                            <tbody aria-live="polite">
                              <tr>
                                <td>
                                  {' '}
                                  <div className="price_table_comp__requirement price_table_comp_color padding_8">
                                    {useTranslation('user_text')}
                                  </div>
                                </td>
                                <td>
                                  <div className="price_table_comp__quantity padding_8">
                                    {useTranslation('est_user_qty_text')}
                                    {` ${userAmount}`}
                                  </div>
                                </td>
                              </tr>
                              <tr>
                                <td>
                                  <div className="price_table_comp__requirement price_table_comp_color padding_8">
                                    {useTranslation('env_text')}
                                  </div>
                                </td>
                                <td>
                                  <div className="price_table_comp__quantity padding_8">
                                    {envAmount}
                                  </div>
                                </td>
                              </tr>
                              <tr>
                                <td>
                                  {' '}
                                  <div className="price_table_comp__requirement price_table_comp_color padding_8">
                                    {useTranslation('work_order_text')}
                                  </div>
                                </td>
                                <td>
                                  <div className="price_table_comp__quantity padding_8">
                                    {estMaintenanceTableData.workOrdersHour}
                                  </div>
                                </td>
                              </tr>
                              <tr>
                                <td>
                                  {' '}
                                  <div className="price_table_comp__requirement price_table_comp_color padding_8">
                                    {useTranslation('health_score_text')}
                                  </div>
                                </td>
                                <td>
                                  <div className="price_table_comp__quantity padding_8">
                                    {estMaintenanceTableData.healthScoresrHour}
                                  </div>
                                </td>
                              </tr>
                              <tr>
                                <td>
                                  {' '}
                                  <div className="price_table_comp__requirement price_table_comp_color padding_8">
                                    {useTranslation('aiService_text')}
                                  </div>
                                </td>
                                <td>
                                  <div className="price_table_comp__quantity padding_8">
                                    {aiService
                                      ? aiServiceVal.max
                                      : aiServiceVal.min}
                                  </div>
                                </td>
                              </tr>
                            </tbody>
                          ) : tileValueSelected === Tiles.tile1 &&
                            whichEssentialPackage > 0 ? (
                            <tbody aria-live="polite">
                              <tr>
                                <td>
                                  {' '}
                                  <div className="price_table_comp__requirement price_table_comp_color padding_8">
                                    {tableText1}
                                  </div>
                                </td>
                                <td>
                                  <div className="price_table_comp__quantity padding_8">
                                    {offering !== switchBrand.brand2 &&
                                      offering !== switchBrand.brand6 &&
                                      useTranslation('est_user_qty_text')}{' '}
                                    {tableVal1}
                                  </div>
                                </td>
                              </tr>
                              {offering !== switchBrand.brand6 && (
                                <tr>
                                  <td>
                                    <div className="price_table_comp__requirement price_table_comp_color padding_8">
                                      {tableText2}
                                    </div>
                                  </td>
                                  <td>
                                    <div className="price_table_comp__quantity padding_8">
                                      {tableVal2}
                                    </div>
                                  </td>
                                </tr>
                              )}

                              {offering === switchBrand.brand6 && (
                                <>
                                  <tr>
                                    <td>
                                      <div className="price_table_comp__requirement price_table_comp_color padding_8">
                                        {tableText2}
                                      </div>
                                    </td>
                                    <td>
                                      <div className="price_table_comp__quantity padding_8">
                                        {tableVal2}
                                      </div>
                                    </td>
                                  </tr>
                                  {/* or
                                  <tr>
                                    <td>
                                      <div className="price_table_comp__requirement price_table_comp_color padding_8">
                                        {tableText3}
                                      </div>
                                    </td>
                                    <td>
                                      <div className="price_table_comp__quantity padding_8">
                                        {tableVal3}
                                      </div>
                                    </td>
                                  </tr> */}
                                </>
                              )}
                              {offering !== switchBrand.brand4 &&
                                offering !== switchBrand.brand6 && (
                                  <tr>
                                    <td>
                                      <div className="price_table_comp__requirement price_table_comp_color padding_8">
                                        {tableText3}
                                      </div>
                                    </td>
                                    <td>
                                      <div className="price_table_comp__quantity padding_8">
                                        {offering !== 'Inspection' &&
                                          useTranslation(
                                            'est_user_qty_text',
                                          )}{' '}
                                        {tableVal3}
                                      </div>
                                    </td>
                                  </tr>
                                )}
                            </tbody>
                          ) : (
                            <tbody aria-live="polite">
                              <tr>
                                <td>
                                  {' '}
                                  <div className="price_table_comp__requirement price_table_comp_color padding_8">
                                    {useTranslation('concurrent_user')}
                                  </div>
                                </td>
                                <td>
                                  <div className="price_table_comp__quantity padding_8">
                                    {concurrentUsersAmt}
                                  </div>
                                </td>
                              </tr>
                              <tr>
                                <td>
                                  <div className="price_table_comp__requirement price_table_comp_color padding_8">
                                    {useTranslation('authorized_user')}
                                  </div>
                                </td>
                                <td>
                                  <div className="price_table_comp__quantity padding_8">
                                    {formattedAuthUsers}
                                  </div>
                                </td>
                              </tr>
                              <tr>
                                <td>
                                  {' '}
                                  <div className="price_table_comp__requirement price_table_comp_color padding_8">
                                    {useTranslation('health_score_text')}
                                  </div>
                                </td>
                                <td>
                                  <div className="price_table_comp__quantity padding_8">
                                    {formattedHealthScores}
                                  </div>
                                </td>
                              </tr>
                              <tr>
                                <td>
                                  {' '}
                                  <div className="price_table_comp__requirement price_table_comp_color padding_8">
                                    {useTranslation('appPoints_text')}
                                  </div>
                                </td>
                                <td>
                                  <div className="price_table_comp__quantity padding_8">
                                    {totalStdBaseAppPoints <=
                                    standardAppPoints.minPoint
                                      ? standardAppPoints.minPoint
                                      : formattedStdBaseAppPoints}
                                  </div>
                                </td>
                              </tr>
                            </tbody>
                          )}
                        </table>
                        <div id="price_toggle_one">
                          <div>
                            {!(
                              concurrentUsersAmt === concurrentUsers.max ||
                              authUsersAmt === authorizedUsers.max ||
                              healthScoresAmt === healthScores.max ||
                              combinedTotalUsers >= COMBINED_USERS
                            ) ? (
                              <>
                                {pkg !== 'Standard' && (
                                  <div>
                                    <p
                                      id="widget.estimated-cost"
                                      className="mas_saas__ibm-type-e estmt-cost price_table_comp__bodycopy padding_24_2"
                                    >
                                      {useTranslation('estimated_text')}
                                      <sup className="mas_saas__sup">1</sup>
                                    </p>

                                    <div className="padding_8 price_table_comp__price_lg_bold">
                                      {loading ? (
                                        <SkeletonPlaceholder className="mas_saas__sticky-nav-skeleton" />
                                      ) : (
                                        `${totalCostMonthly}*`
                                      )}
                                    </div>
                                    <p className="price_table_comp__disclaimer price_table_comp_color padding_24_1">
                                      * {useTranslation('prcie_text')}
                                    </p>
                                    <ButtonSet
                                      className="cta-btn-group"
                                      style={{ display: 'flex' }}
                                    >
                                      <Button
                                        id="request_btn"
                                        onClick={() => {
                                          segmentsTracking('CTA Clicked', {
                                            pageTitle:
                                              'Maximo SaaS Pricing Page',
                                            object: 'Pricing page',
                                            resultValue: 'CTA clicked',
                                            CTA: 'Request a quote now',
                                            location: 'Estimator',
                                            action: '',
                                            field: '',
                                          });
                                          initUrxForm(
                                            environment,
                                            URX_FORM.INSTANCE_ID,
                                            URX_FORM.FORM_ID,
                                            urxlang as IBMLocale,
                                            companyId,
                                            shortcode,
                                            setFormID,
                                          );
                                          setDrawerOpen(true);
                                          setCookie();
                                        }}
                                      >
                                        {useTranslation('quote_text')}
                                      </Button>

                                      {tileValueSelected === Tiles.tile1 &&
                                        showBuy &&
                                        whichEssentialPackage < 2 && (
                                          <Button
                                            renderIcon={Launch16}
                                            id="buynow_btn"
                                            kind="tertiary"
                                            onClick={() => {
                                              segmentsTracking('CTA Clicked', {
                                                pageTitle:
                                                  'Maximo SaaS Pricing Page',
                                                object: 'Pricing page',
                                                resultValue: 'CTA clicked',
                                                CTA: 'Book Now',
                                                location: 'Estimator',
                                                action: '',
                                                field: '',
                                              });
                                              window.open(buyNowUrl);
                                            }}
                                          >
                                            {useTranslation('buy_now')}
                                          </Button>
                                        )}
                                    </ButtonSet>
                                  </div>
                                )}
                              </>
                            ) : !invalidConcurrentUsers &&
                              !invalidHealthScores &&
                              !invalidAuthUsers ? (
                              <>
                                <InlineNotification
                                  kind="info"
                                  role="onprem"
                                  title={premNotificationLabel}
                                  subtitle={useTranslation('contact_info')}
                                  statusIconDescription="On-Prem Info"
                                  hideCloseButton={true}
                                />

                                <ButtonSet className="btn-set mar4 cta-btn-group">
                                  <Button
                                    id="request_btn"
                                    target="_self"
                                    data-ibm-contact="scheduler-link"
                                    href="#"
                                    onClick={() => {
                                      segmentsTracking('CTA Clicked', {
                                        pageTitle: 'Maximo SaaS Pricing Page',
                                        object: 'Pricing page',
                                        resultValue: 'CTA clicked',
                                        CTA: 'Book a Consultation',
                                        location: 'Estimator',
                                        action: '',
                                        field: '',
                                      });
                                      window.open(
                                        ' https://www.ibm.com/products/maximo/pricing/?schedulerform=',
                                        '_self',
                                      );
                                    }}
                                  >
                                    {useTranslation('book_text')}
                                  </Button>
                                </ButtonSet>
                              </>
                            ) : null}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  {tileValueSelected !== Tiles.tile3 && (
                    <div id="footnote" className="div-only-media">
                      <div className="bx--row mar9">
                        <div className="bx--col-lg-9 ">
                          <div className="footnote_wrap">
                            <Footnote numNote={1} />
                            <div className="footnote_text">
                              <DisclaimerText
                                localeCode={localeCode}
                                product={'Maximo Application Suite'}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  <FormModal
                    pageTitle="Maximo SaaS Pricing Page"
                    object="Maximo SaaS Pricing Page"
                    productTitle={'Maximo Application Suite as a Service'}
                    pageUrl={''}
                    location={'Estimator'}
                    bpName={bpName}
                    logo={logo}
                    drawerOpen={drawerOpen}
                    setDrawerOpen={setDrawerOpen}
                    formId={formID}
                    lang={lang}
                  ></FormModal>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MASSaaSPriceEstimator;
